import React, { useState } from 'react';
import { Button, Modal } from '@vacasa/react-components-lib';
import { Alert } from '@mui/material';
import {
    useRevalidateGuestContentByIdsMutation,
    useRevalidateGuestContentCountMutation,
    useRevalidateAllGuestContentMutation,
    useRepublishPublicReviewsWithContentMutation,
} from '../../services/ReviewsService';
import './GuestContentRevalidate.scss';

const uniqNumbers = (values: number[]): number[] => Array.from(new Set(values));

export const GuestContentRevalidate: React.FC = () => {
    const [idsInput, setIdsInput] = useState('');
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);
    const [isConfirmAllOpen, setIsConfirmAllOpen] = useState(false);
    const [isConfirmEmitAllOpen, setIsConfirmEmitAllOpen] = useState(false);
    const [revalidateCount, { isLoading: isCounting }] = useRevalidateGuestContentCountMutation();
    const [revalidateAll, { isLoading: isRevalidatingAll }] = useRevalidateAllGuestContentMutation();
    const [revalidateByIds, { isLoading: isRevalidatingByIds }] = useRevalidateGuestContentByIdsMutation();
    const [emitReviews, { isLoading: isEmitting }] = useRepublishPublicReviewsWithContentMutation();
    const isLoading = isCounting || isRevalidatingAll || isRevalidatingByIds || isEmitting;

    const parseReviewContentIds = (): number[] => {
        return uniqNumbers(
            idsInput
                .split(/[\s,]+/)
                .map((value) => Number(value.trim()))
                .filter((id) => Number.isFinite(id) && id > 0)
        );
    };

    const onEmitAll = async () => {
        try {
            setIsError(false);
            setMessage('');
            await emitReviews().unwrap();
            setMessage('Emit queued. Public reviews with at least one approved photo will be re-published to SNS asynchronously.');
        } catch (_e) {
            setIsError(true);
            setMessage('Unable to start SNS emit. Please try again.');
        }
    };

    const onRevalidateAll = async () => {
        try {
            setIsError(false);
            setMessage('');
            const countResult = await revalidateCount().unwrap();
            const toRevalidate = Number(countResult.to_revalidate) || 0;
            if (toRevalidate === 0) {
                setMessage('No review content rows found to revalidate.');
                return;
            }

            void revalidateAll()
                .unwrap()
                .catch(() => {
                    setIsError(true);
                    setMessage('Unable to start full revalidation. Please try again.');
                });

            setMessage(`Revalidation started for ${toRevalidate} review content rows.`);
        } catch (_e) {
            setIsError(true);
            setMessage('Unable to count review content for revalidation.');
        }
    };

    const onRevalidateByIds = async () => {
        const review_content_ids = parseReviewContentIds();
        if (review_content_ids.length === 0) {
            setIsError(true);
            setMessage('Enter one or more valid review_content_id values.');
            return;
        }

        try {
            setIsError(false);
            setMessage('');
            const result = await revalidateByIds({ review_content_ids }).unwrap();
            const enqueued = result.enqueued?.length ?? 0;
            const notFound = result.not_found?.length ?? 0;
            setMessage(
                `Revalidation started for ${enqueued} review content id(s).${notFound > 0 ? ` ${notFound} id(s) were not found.` : ''}`
            );
        } catch (_e) {
            setIsError(true);
            setMessage('Unable to revalidate the provided review_content_id values.');
        }
    };

    return (
        <div className="guest-content-revalidate">
            <div className="guest-content-revalidate-header">
                <h3>Guest Content</h3>
                <p>
                    Re-emit public reviews that have at least one approved photo to SNS, or reset AI validation and re-queue guest photos
                    (APPROVED / COMPLAINT / REJECTED).
                </p>
            </div>

            {message && <Alert severity={isError ? 'error' : 'success'}>{message}</Alert>}

            <div className="guest-content-revalidate-actions">
                <Button variant="secondary" onClick={() => setIsConfirmEmitAllOpen(true)} disabled={isLoading}>
                    Emit all
                </Button>
            </div>

            <div className="guest-content-revalidate-actions">
                <Button variant="secondary" onClick={() => setIsConfirmAllOpen(true)} disabled={isLoading}>
                    Revalidate all
                </Button>
            </div>

            <div className="field">
                <label>
                    review_content_ids
                    <textarea value={idsInput} onChange={(e) => setIdsInput(e.target.value)} placeholder="101, 102, 103" rows={4} />
                </label>
            </div>

            <div className="guest-content-revalidate-actions">
                <Button variant="secondary" onClick={onRevalidateByIds} disabled={isLoading}>
                    Revalidate by IDs
                </Button>
            </div>

            <Modal showModal={isConfirmEmitAllOpen} setShowModal={setIsConfirmEmitAllOpen} size="medium">
                <div className="confirm-title">Confirm emit all</div>
                <div className="confirm-body">
                    This queues every public review that has guest content with at least one APPROVED photo for SNS re-publish. Reviews are
                    not modified; processing runs asynchronously. This may take time and can generate many events.
                </div>
                <div className="confirm-actions">
                    <Button variant="info" onClick={() => setIsConfirmEmitAllOpen(false)} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={async () => {
                            setIsConfirmEmitAllOpen(false);
                            await onEmitAll();
                        }}
                        disabled={isLoading}
                    >
                        Confirm emit all
                    </Button>
                </div>
            </Modal>

            <Modal showModal={isConfirmAllOpen} setShowModal={setIsConfirmAllOpen} size="medium">
                <div className="confirm-title">Confirm revalidate all</div>
                <div className="confirm-body">
                    This starts a background job that resets every review_contents row to PENDING and re-queues AI validation for each
                    photo. It may take several minutes.
                </div>
                <div className="confirm-actions">
                    <Button variant="info" onClick={() => setIsConfirmAllOpen(false)} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={async () => {
                            setIsConfirmAllOpen(false);
                            await onRevalidateAll();
                        }}
                        disabled={isLoading}
                    >
                        Confirm revalidate all
                    </Button>
                </div>
            </Modal>
        </div>
    );
};
